import { getElementXPath } from '@opentelemetry/sdk-trace-web';
import { InstrumentationBase } from '@opentelemetry/instrumentation';
import * as api from '@opentelemetry/api';
import { stringify } from '@heytap/javascript-stringify';

const MAX_STACK_LENGTH = 5000;
const MAX_MESSAGE_LENGTH = 1000;
const shortenString = (string, limit) => string.length > limit ? `${string.slice(0, limit)}...` : string;
const maybeErrorToObject = (maybeError) => {
    var _a, _b;
    if (maybeError instanceof ErrorEvent) {
        if (maybeError.error) {
            maybeError = maybeError.error;
        }
        else if (maybeError.message) {
            return {
                name: 'ErrorEvent',
                message: maybeError.message,
            };
        }
    }
    return maybeError instanceof Error
        ? {
            name: maybeError.name,
            message: shortenString(maybeError.message, MAX_MESSAGE_LENGTH),
            stack: maybeError.stack
                ? shortenString(maybeError.stack, MAX_STACK_LENGTH)
                : undefined,
        }
        : {
            name: (_a = maybeError === null || maybeError === void 0 ? void 0 : maybeError.name) !== null && _a !== void 0 ? _a : '',
            message: shortenString((_b = stringify(maybeError, null, null, { maxDepth: 1 })) !== null && _b !== void 0 ? _b : '', MAX_MESSAGE_LENGTH),
        };
};
class ApiError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ApiError';
    }
}
class CloudObservationExceptionInstrumentation extends InstrumentationBase {
    constructor(config = {}) {
        super('@heytap/cloud-observation-instrumentation-exception', "2.1.1", config);
        this.isPluginEnabled = true; // enable disable 设置的状态
        const nativeConsoleError = console.error;
        console.error = (...args) => {
            if (this.isPluginEnabled) {
                // zonejs会把Unhandled Promise包装后再通过console.error抛出错误，所以这里需要特殊处理，把错误还原回来
                if (args[0] &&
                    typeof args[0] === 'string' &&
                    args[0].startsWith('Unhandled Promise rejection:')) {
                    return this.recordLog({
                        source: 'unhandledRejection',
                        error: maybeErrorToObject(args[7]),
                    });
                }
                this._onConsoleError(args);
            }
            nativeConsoleError.apply(console, args);
        };
    }
    init() { }
    recordLog(log) {
        var _a, _b, _c, _d, _e, _f, _g;
        const attributes = {};
        if (log.element)
            attributes['element.xpath'] = log.element.xpath;
        if ((_a = log.element) === null || _a === void 0 ? void 0 : _a.src)
            attributes['element.src'] = log.element.src;
        if ((_b = log.element) === null || _b === void 0 ? void 0 : _b.href)
            attributes['element.href'] = log.element.href;
        if (log.source)
            attributes['exception.source'] = log.source;
        if (log.attributes) {
            Object.entries(log.attributes).forEach(([key, val]) => {
                attributes[`ums.rum.custom.${key}`] =
                    typeof val === 'string' ? val : JSON.stringify(val);
            });
        }
        const event = {
            name: log.source.replace(/^\w/, match => match.toUpperCase()),
        };
        if (log.source === 'documentError') {
            event.message = `Document error, xpath: ${(_c = log.element) === null || _c === void 0 ? void 0 : _c.xpath}`;
            if ((_d = log.element) === null || _d === void 0 ? void 0 : _d.src)
                event.message += `, src: ${(_e = log.element) === null || _e === void 0 ? void 0 : _e.src}`;
            else if ((_f = log.element) === null || _f === void 0 ? void 0 : _f.href)
                event.message += `, href: ${(_g = log.element) === null || _g === void 0 ? void 0 : _g.href}`;
        }
        if (log.arguments) {
            const consoleArgumentsString = log.arguments
                .map(argument => stringify(argument, null, null, { maxDepth: 1 }))
                .join(' ');
            const shortenConsoleArgumentsString = shortenString(consoleArgumentsString, MAX_MESSAGE_LENGTH);
            attributes['exception.arguments'] = shortenConsoleArgumentsString;
            event.message = shortenConsoleArgumentsString;
        }
        if (log.error) {
            if (log.error.name)
                event.name = log.error.name;
            if (log.error.message)
                event.message = log.error.message;
            if (log.error.stack)
                event.stack = log.error.stack;
        }
        const startTime = Date.now();
        const span = this.tracer.startSpan('exception', {
            startTime,
            attributes,
        });
        span.recordException(event);
        span.setStatus({ code: api.SpanStatusCode.ERROR });
        api.context.with(api.trace.setSpan(api.context.active(), span), () => {
            span.end(startTime);
        });
    }
    recordCustomError(err, attributes) {
        this.recordLog({
            source: 'customError',
            attributes,
            error: maybeErrorToObject(err),
        });
    }
    recordApiError(errMsg, attributes) {
        if (typeof errMsg !== 'string')
            return;
        const apiError = new ApiError(errMsg);
        this.recordLog({
            source: 'apiError',
            attributes,
            error: apiError,
        });
    }
    _onError(error) {
        this.recordLog({
            source: 'uncaughtException',
            error: maybeErrorToObject(error),
        });
    }
    _onUnhandledRejection(event) {
        const { reason } = event;
        if (!reason)
            return;
        this.recordLog({
            source: 'unhandledRejection',
            error: maybeErrorToObject(reason),
        });
    }
    _onDocumentError({ target }) {
        if (!target)
            return;
        const xpath = getElementXPath(target);
        // @ts-expect-error
        const src = target === null || target === void 0 ? void 0 : target.src;
        // @ts-expect-error
        const href = target === null || target === void 0 ? void 0 : target.href;
        if (!xpath)
            return;
        this.recordLog({
            source: 'documentError',
            element: { xpath, src, href },
        });
    }
    _onConsoleError(args) {
        const error = args.find(arg => arg instanceof Error);
        this.recordLog({
            arguments: args,
            source: 'consoleError',
            error: error ? maybeErrorToObject(error) : undefined,
        });
    }
    enable() {
        this.disable();
        this._onDocumentError = this._onDocumentError.bind(this);
        this._onError = this._onError.bind(this);
        this._onUnhandledRejection = this._onUnhandledRejection.bind(this);
        // 发现问题在于引入的js资源于当前网址不同源导致的，unhandledrejection跟随同源策略
        window.addEventListener('unhandledrejection', this._onUnhandledRejection);
        // JS 运行时错误发生时，window 会触发一个 ErrorEvent 接口的 error 事件，并执行 window.onerror();
        // 当一项资源（如图片或脚本）加载失败，加载资源的元素会触发一个 Event 接口的 error 事件，并执行该元素上的onerror() 处理函数。
        // 这些 error 事件不会向上冒泡到 window ，不过（至少在 Firefox 中）能被单一的window.addEventListener 捕获。不论是静态资源异常，或者接口异常，错误都无法捕获到。
        window.addEventListener('error', this._onError); // error事件是用来监听DOM操作错误DOMException和JS错误告警的
        document.documentElement.addEventListener('error', this._onDocumentError, {
            capture: true,
        });
        this.isPluginEnabled = true;
    }
    disable() {
        window.removeEventListener('error', this._onError);
        window.removeEventListener('unhandledrejection', this._onUnhandledRejection);
        document.documentElement.removeEventListener('error', this._onDocumentError, { capture: true });
        this.isPluginEnabled = false;
    }
}

export { CloudObservationExceptionInstrumentation };
